import { FC, MouseEvent } from 'react';
import { LinkProps, useHistory } from 'react-router-dom';
import { ButtonProps } from 'antd/lib/button';

export type ButtonLinkProps = ButtonProps & {
    download?: boolean;
    href?: string;
    onLinkClick?: LinkProps['onClick'];
    target?: string;
    to?: LinkProps['to'];
};

const ButtonLink: FC<ButtonLinkProps> = ({ children, download, href, onLinkClick, target, to, onClick, ...rest }) => {
    const history = useHistory();
    const resolvedTo = typeof to === 'function' ? to(history.location) : to;
    const resolvedHref =
        href ?? (typeof resolvedTo === 'string' ? `${process.env.PUBLIC_URL ?? ''}${resolvedTo}` : undefined);
    const { block, className, disabled, htmlType, size, type } = rest;

    const handleInternalClick = (event: MouseEvent<HTMLElement>) => {
        onClick?.(event as Parameters<NonNullable<ButtonProps['onClick']>>[0]);
        onLinkClick?.(event as Parameters<NonNullable<LinkProps['onClick']>>[0]);

        if (event.defaultPrevented || !resolvedTo) {
            return;
        }

        if (target === '_blank') {
            const targetUrl = typeof resolvedTo === 'string' ? resolvedTo : history.createHref(resolvedTo);

            window.open(targetUrl, '_blank', 'noopener,noreferrer');
            return;
        }

        history.push(resolvedTo);
    };

    const classes = [
        'ant-btn',
        type === 'primary' ? 'ant-btn-primary' : '',
        type === 'dashed' ? 'ant-btn-dashed' : '',
        type === 'link' ? 'ant-btn-link' : '',
        type === 'text' ? 'ant-btn-text' : '',
        size === 'large' ? 'ant-btn-lg' : '',
        size === 'small' ? 'ant-btn-sm' : '',
        block ? 'ant-btn-block' : '',
        className ?? '',
    ]
        .filter(Boolean)
        .join(' ');

    if (resolvedHref) {
        const handleAnchorClick = (event: MouseEvent<HTMLAnchorElement>) => {
            onClick?.(event as Parameters<NonNullable<ButtonProps['onClick']>>[0]);
            onLinkClick?.(event as Parameters<NonNullable<LinkProps['onClick']>>[0]);

            if (disabled) {
                event.preventDefault();
            }
        };

        return (
            <a
                className={classes}
                download={download}
                href={resolvedHref}
                onClick={handleAnchorClick}
                rel={target === '_blank' ? 'noopener noreferrer' : undefined}
                target={target}
            >
                {children}
            </a>
        );
    }

    return (
        <button type={htmlType ?? 'button'} className={classes} disabled={disabled} onClick={handleInternalClick}>
            {children}
        </button>
    );
};

export default ButtonLink;
